import { useSelector } from "react-redux";
import { useLogin } from "../PortalRunner/hooks/useLogin";
import { selectRefData } from "../Store/api/refData/refDataSelectors";
import { selectDynamicFormList } from "../Store/api/dynamicForm/dynamicFormSelectors";
import { selectCurrentUser } from "../Store/api/auth/authSelectors";
import {
  selectProgram,
  selectProgramResourceStrings,
} from "../Store/api/program/programSelectors";
import { useGetProgramEventListQuery } from "../Store/api/apiSlice";
import {
  USER_APPLICATION_SESSION_KEY,
  domainKey,
} from "../MPP-Common-SPA/utils/globalUtility";
import { getCurrentDomain } from "./auth";
import { setVerificationAttempted } from "../MPP-Common-SPA/utils/verificationNotificationUtils";
import { InternalApplicationStatuses } from "../MPP-Common-SPA/common/constants/statuses";
import { useUnsavedChanges } from "../MPP-Common-SPA/common/unsavedChangesContext";

const RESOURCES_PREFIX = "RESOURCES";
const RESOURCES_SPLIT = "@";

const SAVE_DRAFT_ENDPOINT = "/api/application/saveApplicationAsDraft";
const SAVE_APPLICATION_DATA_ENDPOINT = "/api/application/saveApplicationData";

export const useSessionManagement = () => {
  const user = useSelector(selectCurrentUser);
  const refData = useSelector(selectRefData);
  const dynamicForms = useSelector(selectDynamicFormList);
  const resourceStrings = useSelector(selectProgramResourceStrings);
  const program = useSelector(selectProgram);
  const { data: programEvents } = useGetProgramEventListQuery(program?.id, {
    skip: !program?.id,
  });

  const { handleLogin } = useLogin();
  const { clearDirty } = useUnsavedChanges();

  const verifyAccount = async () => {
    setVerificationAttempted();
    let callbackUrl = `${getCurrentDomain()}`;

    // We are on the my applications page. Redirect back here.
    switch(window.location.pathname){
      case "/applications":
        callbackUrl += window.location.pathname;
        clearDirty();
        return handleLogin(callbackUrl);
    }

    // Save as draft
    try {
      // Need to cache the domain key to prevent the getter from running during post. This would result in early state updates.
      const currentDomainKey = domainKey;

      let userApplicationData = {};
      let applicationId = null;
      let applicationTypeId = null;
      let uniqueApplicationCode = null;
      let internalStatus = null;

      try {
        // Get current application data from sessionStorage
        const storedData =
          JSON.parse(sessionStorage.getItem(USER_APPLICATION_SESSION_KEY)) ??
          {};

        if (Object.keys(storedData).length > 0) {
          userApplicationData = storedData;
          applicationId = storedData.applicationId;
          applicationTypeId = storedData.applicationTypeId;
          uniqueApplicationCode = storedData.uniqueApplicationCode;
          internalStatus = storedData.internalStatus;
        }
      } catch (error) {
        console.warn(
          "Failed to get application data from sessionStorage:",
          error
        );
      }

      // Draft/new applications save as draft; non-draft applications save data without status change.
      const isDraft = !internalStatus || internalStatus === InternalApplicationStatuses.Draft;
      const endpoint = isDraft ? SAVE_DRAFT_ENDPOINT : SAVE_APPLICATION_DATA_ENDPOINT;

      if (Object.keys(userApplicationData).length > 0) {
        try {
            const saveRequest = {
              ApplicationId: applicationId,
              ApplicationTypeId: applicationTypeId,
              Application: JSON.stringify(userApplicationData)
            };
            
            // Use RTK?
            const response = await fetch(
              endpoint,
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  "domain-key": currentDomainKey,
                },
                body: JSON.stringify(saveRequest)
              }
            );

            if (response.ok) {
              const responseObj = await response.json();
              if (responseObj?.data?.uniqueApplicationCode) {
                uniqueApplicationCode = responseObj.data.uniqueApplicationCode;
              }
            }
        } catch (error) {
          console.error('Failed to save draft:', error);
        }
      }

      // Use handleLogin with application code as a parameter
      clearDirty();
      if (uniqueApplicationCode) {
        callbackUrl = `${getCurrentDomain()}/application?code=${encodeURIComponent(uniqueApplicationCode)}`;
        handleLogin(callbackUrl);
      } else {
        handleLogin(); // Use default callback
      }
    } catch (error) {
      // If anything fails, redirect to saml login
      clearDirty();
      handleLogin();
    }
  };

  const clickHandlers = {
    "handleLogin": handleLogin,
    "verifyAccount": verifyAccount
  };

  const getResourceValue = (resourceStrings, key, params = {}) => {
    if (!key || !resourceStrings) {
      return key || '';
    }

    const lst = key.split(RESOURCES_SPLIT);

    if (lst[0] !== RESOURCES_PREFIX) {
      console.error(`Invalid resources key ${key}`);
      return key;
    }

    const resourceKey = lst[1];

    if (resourceKey === 'undefined') {
      return '';
    }

    const v = resourceStrings.find((x) => x.key === resourceKey);
    if (!v) return resourceKey;

    let value = v.value;

    // Replace placeholders in the value with provided params
    Object.keys(params).forEach((paramKey) => {
      const paramValue = params[paramKey];
      const regex = new RegExp(`{${paramKey}}`, 'g');
      value = value.replace(regex, paramValue);
    });

    return value;
  };

  const getDynamicForm = (dynamicForms, code, contains = false) => {
    if (!code || !dynamicForms) return null;
    if (contains) {
      return dynamicForms.find((fm) => fm.code?.includes(code));
    }
    return dynamicForms.find((fm) => fm.code === code);
  };

  const getRefData = (refData, category, domainKey) => {
    if (!refData || !category) return [];

    return refData?.filter(
      (ref) =>
        ref.categoryCode === category &&
        (!ref.domainKey || ref.domainKey === domainKey)
    );
  };

  return {
    getDynamicForm: (code, contains = false) =>
      getDynamicForm(dynamicForms, code, contains),

    getRefData: ({ category, domainKey }) =>
      getRefData(refData, category, domainKey),

    getResourceValue: ({ key, params = {} }) =>
      getResourceValue(resourceStrings, key, params),

    clickHandlers,
    programEvents,
    program,
  };
};
